import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Alert,
} from "reactstrap";

/** Import Full Calendar  */
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import BootstrapTheme from "@fullcalendar/bootstrap";
import listPlugin from '@fullcalendar/list';
import type { EventClickArg, EventMountArg } from "@fullcalendar/core";

//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

import { useBookingCalendarOrders } from "../../hooks/useBookingCalendarOrders";
import type { OrderNode } from "../../graphql/queries/orders";
import {
  BookingCalendarBucket,
  buildBookingCalendarEvents,
} from "../../utils/bookingCalendarEvents";
import {
  detectOrderType,
  getOrderLineItemDisplayName,
  metaArrayToRecord,
  type LineItemOrderType,
} from "../../utils/orderLineItemMeta";

interface SelectedBookingSummary {
  bucket: BookingCalendarBucket;
  dateKey: string;
  orderIds: number[];
}

const STATUS_DISPLAY: Record<
  BookingCalendarBucket,
  { label: string; color: string }
> = {
  confirmed: { label: "Confirmed", color: "success" },
  pending: { label: "Pending", color: "warning" },
  cancelled: { label: "Cancelled", color: "danger" },
};

const TYPE_DISPLAY: Record<
  LineItemOrderType,
  { label: string; color: string; icon: string }
> = {
  hotel: { label: "Hotel", color: "info", icon: "mdi-office-building" },
  tour: { label: "Tour", color: "success", icon: "mdi-palm-tree" },
  transfer: { label: "Transfer", color: "primary", icon: "mdi-bus" },
  package: { label: "Package", color: "secondary", icon: "mdi-package-variant" },
  generic: { label: "Package", color: "secondary", icon: "mdi-package-variant" },
};

function formatSummaryDate(dateKey: string): string {
  return new Date(`${dateKey}T12:00:00`).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatShortDate(value: string): string {
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00`)
    : new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getOrderDateLabel(order: OrderNode): string {
  for (const item of order.lineItems?.nodes ?? []) {
    const meta = metaArrayToRecord(item.metaData);
    const start =
      meta._ngk_check_in ||
      meta["Booking Date"] ||
      meta.tat_fields_departure_date ||
      meta.tat_fields_arrival_date;
    const end = meta._ngk_check_out;
    if (start && end) return `${formatShortDate(start)} – ${formatShortDate(end)}`;
    if (start) return formatShortDate(start);
  }
  return order.date ? formatShortDate(order.date) : "Date unavailable";
}

function getOrderDisplayItems(order: OrderNode) {
  const items = (order.lineItems?.nodes ?? []).map((item) => {
    const productName = item.product?.node?.name || "Booking";
    const meta = metaArrayToRecord(item.metaData);
    const type = detectOrderType(meta, productName);
    return {
      type,
      name: getOrderLineItemDisplayName(productName, item.metaData),
    };
  });

  return Array.from(
    new Map(items.map((item) => [`${item.type}-${item.name}`, item])).values()
  );
}

const BookingCalendar = () => {
  const [selectedSummary, setSelectedSummary] =
    useState<SelectedBookingSummary | null>(null);
  const { orders, loading: ordersLoading, error: ordersError, refetch } =
    useBookingCalendarOrders();

  const calendarEvents = useMemo(
    () => buildBookingCalendarEvents(orders),
    [orders]
  );

  const selectedOrders = useMemo(() => {
    if (!selectedSummary) return [];
    const selectedIds = new Set(selectedSummary.orderIds);
    return orders.filter((order) => selectedIds.has(order.databaseId));
  }, [orders, selectedSummary]);

  const handleEventClick = (arg: EventClickArg) => {
    const { bucket, dateKey, orderIds } = arg.event
      .extendedProps as SelectedBookingSummary;
    setSelectedSummary({ bucket, dateKey, orderIds });
  };

  const markEventClickable = (arg: EventMountArg) => {
    arg.el.style.cursor = "pointer";
    arg.el.title = "Click to view booking details";
  };

  const closeDetails = () => setSelectedSummary(null);
  const selectedStatus = selectedSummary
    ? STATUS_DISPLAY[selectedSummary.bucket]
    : STATUS_DISPLAY.pending;

  document.title = "Booking Calendar | NGK Tours";
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Apps" breadcrumbItem="Booking Calendar" />
          <Row>
            <Col xs={12}>
              <Row>
                <Col xs={12}>
                  <Card>
                    <CardBody>
                      {ordersError && (
                        <Alert color="danger" className="mb-3">
                          Could not load bookings.{" "}
                          <Button color="link" className="p-0 align-baseline" onClick={() => refetch()}>
                            Retry
                          </Button>
                        </Alert>
                      )}
                      {ordersLoading && (
                        <div className="text-center py-4">
                          <Spinner color="primary" />
                          <p className="text-muted mt-2 mb-0">Loading bookings…</p>
                        </div>
                      )}
                      <FullCalendar
                        plugins={[
                          BootstrapTheme,
                          dayGridPlugin,
                          interactionPlugin,
                          listPlugin
                        ]}
                        slotDuration={"00:15:00"}
                        handleWindowResize={true}
                        themeSystem="bootstrap"
                        headerToolbar={{
                          left: "prev,next today",
                          center: "title",
                          right: "dayGridMonth,dayGridWeek,dayGridDay,listWeek",
                        }}
                        events={calendarEvents}
                        editable={false}
                        droppable={false}
                        selectable={false}
                        eventClick={handleEventClick}
                        eventDidMount={markEventClickable}
                      />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={selectedSummary !== null} toggle={closeDetails} size="lg" centered>
        <ModalHeader toggle={closeDetails}>
          <div className="d-flex align-items-center">
            <span
              className={`bg-${selectedStatus.color} rounded-circle me-2`}
              style={{ width: 16, height: 16, display: "inline-block" }}
            />
            <div>
              <div>{selectedStatus.label} bookings</div>
              {selectedSummary && (
                <div className="text-muted fw-normal font-size-13 mt-1">
                  {formatSummaryDate(selectedSummary.dateKey)} ·{" "}
                  {selectedOrders.length}{" "}
                  {selectedOrders.length === 1 ? "booking" : "bookings"}
                </div>
              )}
            </div>
          </div>
        </ModalHeader>

        <ModalBody>
          {selectedOrders.length === 0 ? (
            <p className="text-muted text-center py-4 mb-0">
              No booking details are available for this status.
            </p>
          ) : (
            selectedOrders.map((order) => {
              const items = getOrderDisplayItems(order);
              const customerName =
                [order.billing?.firstName, order.billing?.lastName]
                  .filter(Boolean)
                  .join(" ") ||
                order.billing?.email ||
                "Unknown customer";

              return (
                <div className="border rounded p-3 mb-3" key={order.databaseId}>
                  <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
                    <div className="flex-grow-1">
                      {items.map((item) => {
                        const typeDisplay = TYPE_DISPLAY[item.type];
                        return (
                          <div
                            className="d-flex align-items-center gap-2 mb-1"
                            key={`${item.type}-${item.name}`}
                          >
                            <span
                              className={`bg-${typeDisplay.color}-subtle text-${typeDisplay.color} rounded d-inline-flex align-items-center justify-content-center`}
                              style={{ width: 34, height: 34, flexShrink: 0 }}
                            >
                              <i className={`mdi ${typeDisplay.icon} font-size-20`} />
                            </span>
                            <div>
                              <Badge color={typeDisplay.color} pill className="me-2">
                                {typeDisplay.label}
                              </Badge>
                              <span className="fw-semibold">{item.name}</span>
                            </div>
                          </div>
                        );
                      })}
                      <div className="text-muted font-size-13 ms-md-5">
                        Booked by <span className="text-body">{customerName}</span>
                        {order.billing?.email &&
                          customerName !== order.billing.email &&
                          ` · ${order.billing.email}`}
                      </div>
                    </div>

                    <div className="font-size-13" style={{ minWidth: 155 }}>
                      <div className="text-muted">Booking date</div>
                      <div>{getOrderDateLabel(order)}</div>
                    </div>

                    <div className="d-flex flex-md-column align-items-center align-items-md-end gap-2">
                      <Badge color={selectedStatus.color}>
                        {(order.status || selectedStatus.label)
                          .replace(/_/g, " ")
                          .toUpperCase()}
                      </Badge>
                      <Link
                        to={`/bookings/${order.databaseId}`}
                        className="btn btn-primary btn-sm"
                      >
                        View #{order.orderNumber ?? order.databaseId}
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="light" onClick={closeDetails}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default BookingCalendar;
